import { useState, type FormEvent } from 'react';
import { Box, Button, Field, Heading, Input, Stack, Text, Textarea } from '@chakra-ui/react';
import { RequiredLabel, RequiredLegend } from '@/components/ui/required-field';

export type SellerFormValues = {
  fullName: string;
  email: string;
  phone: string;
  notes: string;
};

export const EMPTY_SELLER_FORM: SellerFormValues = {
  fullName: '',
  email: '',
  phone: '',
  notes: '',
};

type Props = {
  initial: SellerFormValues;
  submitLabel: string;
  submitting: boolean;
  error?: string | null;
  onSubmit: (values: SellerFormValues) => void | Promise<void>;
  onCancel: () => void;
};

export function SellerForm({ initial, submitLabel, submitting, error, onSubmit, onCancel }: Props) {
  const [values, setValues] = useState<SellerFormValues>(initial);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    await onSubmit(values);
  }

  return (
    <Box bg="bg.surface" borderWidth="1px" borderColor="border" rounded="lg" p={{ base: '4', md: '6' }}>
      <Heading size="md" mb="5">
        Datos del vendedor
      </Heading>
      <form onSubmit={handleSubmit}>
        <Stack gap="4">
          <Field.Root required>
            <RequiredLabel>Nombre completo</RequiredLabel>
            <Input
              value={values.fullName}
              onChange={(e) => setValues({ ...values, fullName: e.currentTarget.value })}
              placeholder="Ana Gómez"
              maxLength={200}
            />
          </Field.Root>

          <Stack direction={{ base: 'column', md: 'row' }} gap="4" align="flex-start">
            <Field.Root flex="1">
              <Field.Label>Teléfono</Field.Label>
              <Input
                value={values.phone}
                onChange={(e) => setValues({ ...values, phone: e.currentTarget.value })}
                placeholder="+502 5555 0000"
                maxLength={30}
              />
            </Field.Root>
            <Field.Root flex="1">
              <Field.Label>Correo</Field.Label>
              <Input
                type="email"
                value={values.email}
                onChange={(e) => setValues({ ...values, email: e.currentTarget.value })}
                placeholder="ana@ventas.com"
                maxLength={200}
              />
            </Field.Root>
          </Stack>

          <Field.Root>
            <Field.Label>Notas</Field.Label>
            <Textarea
              value={values.notes}
              onChange={(e) => setValues({ ...values, notes: e.currentTarget.value })}
              placeholder="Notas internas sobre el vendedor."
              rows={3}
            />
          </Field.Root>

          {error && (
            <Text color="red.600" fontSize="sm">
              {error}
            </Text>
          )}

          <RequiredLegend />

          <Stack direction={{ base: 'column-reverse', md: 'row' }} gap="3" justify="flex-end">
            <Button variant="ghost" onClick={onCancel} disabled={submitting} w={{ base: 'full', md: 'auto' }}>
              Cancelar
            </Button>
            <Button
              type="submit"
              colorPalette="brand"
              bg="brand.600"
              color="white"
              _hover={{ bg: 'brand.700' }}
              loading={submitting}
              loadingText="Guardando…"
              w={{ base: 'full', md: 'auto' }}
            >
              {submitLabel}
            </Button>
          </Stack>
        </Stack>
      </form>
    </Box>
  );
}
